import { useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { IFFFChangeHandlerEvent, IFieldState } from '../../interfaces';
import { isReactChangeEvent } from '../utils/isReactChangeEvent';
import { trackFieldInteractionEvent } from '../utils/segment';

const useFieldState = (
  validators: [(field: string) => string] | [],
  // "fieldName" is required to identify the field on the hooks consumed by this class
  fieldName: string,
  normalizer?: (currentValue: string, previousValue: string) => string,
  defaultValue = '',
): IFieldState => {
  const [value, setValue] = useState(defaultValue);
  const [previousValue, setPreviousValue] = useState(defaultValue);
  const [isTouched, setIsTouched] = useState(false);
  const [lastPostSubmitError, setLastPostSubmitError] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const changeHandler = (event: IFFFChangeHandlerEvent) => {
    if (isReactChangeEvent(event) && event.target) {
      const current = event.target.value;
      const normalizedValue = normalizer ? normalizer(current, value) : current;
      event.preventDefault();
      setPreviousValue(value);
      setValue(normalizedValue);
      setIsTouched(true);
      setErrorMsg(`${validators.map((validator) => `${validator(normalizedValue)} `)}`.trim());

      event.target.value = normalizedValue;
    }
  };

  const blurHandler = () => {
    setIsTouched(true);
    setErrorMsg(`${validators.map((validator) => `${validator(value)} `)}`.trim());
  };

  const focusHandler = (eventData?: { fieldName: string; section: string; formType: string }) => {
    datadogLogs.logger.info(`Focus on ${fieldName.toUpperCase()} input`);

    if (eventData) {
      trackFieldInteractionEvent({
        fieldName: eventData.fieldName,
        section: eventData.section,
        formType: eventData.formType,
        action: 'focus',
      });
    }
  };

  const onSubmit = (error: string) => {
    setErrorMsg(error);
    setLastPostSubmitError(error);
  };

  const reset = () => {
    setValue(defaultValue);
    setIsTouched(false);
    setErrorMsg('');
    setPreviousValue(defaultValue);
    setLastPostSubmitError('');
  };

  return {
    value,
    previousValue,
    errorMsg,
    hasError: errorMsg !== '' && isTouched,
    isTouched,
    changeHandler,
    blurHandler,
    focusHandler,
    reset,
    lastPostSubmitError,
    onSubmit,
  };
};

export default useFieldState;
